import {
  DispatchTaskAssignmentView,
  TaskAssignmentOrEvent,
  TaskEvent,
} from '@dispatch/shared/types';

/**
 * Tie-breaker priority for events that share an identical millisecond
 * timestamp. Lower number wins (renders first). Codes not listed here
 * default to 0, which together with ES2019's stable Array.prototype.sort
 * means their original insertion order is preserved.
 *
 * The 'assignment-unlocked' < 'assignment-locked' ordering exists because
 * force-reassigning an already-locked task emits the Unlocked + Locked
 * pair with the same timestamp; the audit timeline should always show
 * Unlocked first to reflect the true cause-and-effect order.
 */
export const EVENT_TIE_BREAK_ORDER: Record<string, number> = {
  'assignment-unlocked': 1,
  'assignment-locked': 2,
};

// Re-export for callers that want to import alongside the sort util.
export type { TaskAssignmentOrEvent };

const isDispatchTaskAssignmentView = (
  obj: TaskAssignmentOrEvent
): obj is DispatchTaskAssignmentView => {
  return 'assignmentAction' in obj;
};

const isTaskEvent = (obj: TaskAssignmentOrEvent): obj is TaskEvent => {
  return 'eventDateTime' in obj;
};

const getDateString = (item: TaskAssignmentOrEvent): string | null => {
  if (isDispatchTaskAssignmentView(item)) {
    const dateTime = item.assignmentAction?.dateTime;
    return dateTime ? String(dateTime) : null;
  }
  if (isTaskEvent(item)) {
    return item.eventDateTime ?? null;
  }
  return null;
};

const getEventCode = (item: TaskAssignmentOrEvent): string | undefined => {
  if (isTaskEvent(item)) {
    return item.eventCode;
  }
  if (isDispatchTaskAssignmentView(item)) {
    const actionType = item.assignmentAction?.actionType;
    return actionType !== undefined ? String(actionType) : undefined;
  }
  return undefined;
};

/**
 * Sort merged task assignment + event entries by timestamp ascending,
 * applying EVENT_TIE_BREAK_ORDER when two entries share the same
 * millisecond timestamp. Returns a new array (never mutates input).
 */
export function sortMergedTaskEvents<
  T extends TaskEvent | DispatchTaskAssignmentView
>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const dateA = getDateString(a);
    const dateB = getDateString(b);

    const parsedA = dateA ? new Date(dateA).getTime() : 0;
    const parsedB = dateB ? new Date(dateB).getTime() : 0;

    if (parsedA !== parsedB) {
      return parsedA - parsedB;
    }

    // Same timestamp: apply the tie-break priority map. Unmapped codes
    // resolve to 0 which, combined with ES2019 stable sort, preserves
    // their original insertion order.
    const codeA = getEventCode(a);
    const codeB = getEventCode(b);
    const priorityA = codeA ? EVENT_TIE_BREAK_ORDER[codeA] ?? 0 : 0;
    const priorityB = codeB ? EVENT_TIE_BREAK_ORDER[codeB] ?? 0 : 0;

    return priorityA - priorityB;
  });
}
